/* eslint-disable */
// @ts-nocheck

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTypedSelector } from 'helpers';
import { hasWriteAccessToMasterConfigSubSystem, hasWriteAccessToFaresSubSystem, hasWriteAccessToOperationsSubSystem } from 'selectors';

/**
 * ViewEntity - Reusable component for viewing entity details with dynamic fields
 * 
 * @param {Object} props
 * @param {string} props.pageTitle - Translation key for page title (e.g., 'BankInfo')
 * @param {string} props.fetchApiUrl - API URL for fetching entity data (can include :id placeholder)
 * @param {string} props.fetchApiMethod - HTTP method for fetch API (default: 'GET')
 * @param {Function} props.buildFetchPayload - Function to build fetch payload (for POST requests)
 * @param {string} props.dataPath - Path to data in response (default: 'data.data')
 * @param {Array} props.fields - Array of field configurations to display
 * @param {Array} props.additionalApis - Array of additional API calls to make after main fetch
 * @param {string} props.editUrl - URL pattern for edit page (can include :id placeholder)
 * @param {string} props.deleteApiUrl - API URL for delete operation
 * @param {string} props.deleteApiMethod - HTTP method for delete API (default: 'DELETE')
 * @param {Function} props.buildDeletePayload - Function to build delete payload
 * @param {string} props.backUrl - URL to navigate back to
 * @param {string} props.backButtonLabel - Translation key for back button label
 * @param {Function} props.buildTitle - Function to build title from entity data
 * @param {Function} props.buildDeleteConfirmMessage - Function to build delete confirmation message
 * @param {boolean} props.showEditButton - Show edit button (default: true)
 * @param {boolean} props.showDeleteButton - Show delete button (default: true)
 * @param {string} props.subsystemName - Name of subsystem for permission check (optional)
 * @param {string} props.permissionSystem - Permission system type: 'fares', 'operations', 'masterConfig' (default: 'masterConfig')
 * @param {Function} props.canDelete - Function to determine if entity can be deleted
 * 
 * Field Configuration:
 * {
 *   name: string,              // Field name/key in data
 *   labelKey: string,          // Translation key for label
 *   type: string,              // Field type: 'text', 'date', 'status', 'custom'
 *   render: Function,          // Custom render function (data, intl) => ReactNode
 *   hide: Function,            // Function to determine if field should be hidden (data) => boolean
 * }
 * 
 * Additional API Configuration:
 * {
 *   name: string,              // Name for storing the result
 *   url: string | Function,    // API URL or function that returns URL based on mainData
 *   method: string,            // HTTP method (default: 'GET')
 *   payload: Object | Function,// Payload or function that returns payload based on mainData
 *   dataPath: string,          // Path to data in response (default: 'data.data')
 *   onSuccess: Function,       // Callback after successful fetch (data, mainData) => void
 * }
 */
const ViewEntity = ({
  pageTitle,
  fetchApiUrl,
  fetchApiMethod = 'GET',
  buildFetchPayload,
  dataPath = 'data.data',
  fields = [],
  additionalApis = [],
  editUrl,
  deleteApiUrl,
  deleteApiMethod = 'DELETE',
  buildDeletePayload,
  backUrl,
  backButtonLabel = 'Back',
  buildTitle,
  buildDeleteConfirmMessage,
  showEditButton = true,
  showDeleteButton = true,
  subsystemName,
  permissionSystem = 'masterConfig',
  canDelete,
  renderAboveData,
  renderBelowData,
  renderActionButtons,
  messageTransformer,
  containerStyle,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const { id, org_id, bank_id } = useParams();
  const currentLanguageCode = localStorage.getItem("EASY_MOBILITY_LANGUAGE") || 'en-us';
  
  // Check write access if subsystem is provided
  const hasWriteAccess = subsystemName
    ? (() => {
        switch (permissionSystem) {
          case 'fares':
            return useTypedSelector(state => hasWriteAccessToFaresSubSystem(state, subsystemName));
          case 'operations':
            return useTypedSelector(state => hasWriteAccessToOperationsSubSystem(state, subsystemName));
          case 'masterConfig':
          default:
            return useTypedSelector(state => hasWriteAccessToMasterConfigSubSystem(state, subsystemName));
        }
      })()
    : true;
  
  console.log('[ViewEntity] hasWriteAccess:', hasWriteAccess, 'subsystemName:', subsystemName, 'permissionSystem:', permissionSystem);
  
  // Auth data
  let userAuthData = '';
  const authData = localStorage.getItem("authData");
  if (authData !== null) {
    const parsedAuthData = JSON.parse(authData);
    userAuthData = parsedAuthData.userToken;
  }

  // State
  const [entityData, setEntityData] = useState(null);
  const [additionalData, setAdditionalData] = useState({});
  const [isFormLoading, setIsFormLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [deleteResponse, setDeleteResponse] = useState(null);

  // Fetch entity data
  useEffect(() => {
    const fetchEntity = async () => {
      setIsFormLoading(true);
      setError(null);
      
      try {
        // Replace placeholders in URL
        let url = fetchApiUrl
          .replace(':id', id)
          .replace(':org_id', org_id)
          .replace(':bank_id', bank_id);
        
        url = url.startsWith('http') ? url : `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}${url}`;

        const config = {
          headers: {
            'access-token': userAuthData?.['access-token'] || userAuthData?.access_token,
            uid: userAuthData?.uid,
            client: userAuthData?.client,
          },
        };

        let response;
        if (fetchApiMethod.toUpperCase() === 'POST') {
          const payload = buildFetchPayload ? buildFetchPayload({ id, org_id, bank_id }) : {};
          response = await axios.post(url, payload, config);
        } else {
          response = await axios.get(url, config);
        }

        // Extract data based on dataPath
        const pathParts = dataPath.split('.');
        let data = response;
        
        for (const part of pathParts) {
          data = data[part];
        }

        console.log('[ViewEntity] Fetched data:', data);
        setEntityData(data);

        // Fetch additional APIs if configured
        if (additionalApis.length > 0) {
          await fetchAdditionalApis(data);
        }
      } catch (error) {
        const errorMsg = `Failed to fetch entity: ${error?.response?.data?.message || error.toString()}`;
        setError(errorMsg);
        /*toast.error(errorMsg, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
        }); */
      } finally {
        setIsFormLoading(false);
      }
    };

    fetchEntity();
  }, [deleteResponse, id, org_id, bank_id]);

  // Fetch additional APIs
  const fetchAdditionalApis = async (mainData) => {
    const results = {};
    
    for (const apiConfig of additionalApis) {
      try {
        // Resolve URL
        let url = typeof apiConfig.url === 'function'
          ? apiConfig.url(mainData)
          : apiConfig.url;
        
        url = url.startsWith('http') ? url : `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}${url}`;

        const config = {
          headers: {
            'access-token': userAuthData?.['access-token'] || userAuthData?.access_token,
            uid: userAuthData?.uid,
            client: userAuthData?.client,
          },
        };

        let response;
        const method = (apiConfig.method || 'GET').toUpperCase();
        
        if (method === 'POST') {
          const payload = typeof apiConfig.payload === 'function'
            ? apiConfig.payload(mainData)
            : apiConfig.payload || {};
          response = await axios.post(url, payload, config);
        } else {
          response = await axios.get(url, config);
        }

        // Extract data based on dataPath
        const apiDataPath = apiConfig.dataPath || 'data.data';
        const pathParts = apiDataPath.split('.');
        let data = response;
        
        for (const part of pathParts) {
          data = data[part];
        }

        console.log(`[ViewEntity] Additional API ${apiConfig.name}:`, data);
        results[apiConfig.name] = data;

        // Call onSuccess callback if provided
        if (apiConfig.onSuccess) {
          apiConfig.onSuccess(data, mainData);
        }
      } catch (error) {
        console.error(`[ViewEntity] Error fetching ${apiConfig.name}:`, error);
        results[apiConfig.name] = null;
      }
    }

    setAdditionalData(results);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteApiUrl) {
      toast.error('Delete API not configured', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        style: { direction: 'ltr', textAlign: 'left', fontFamily: 'Cairo, sans-serif', fontWeight: 'bold' },
      });
      return;
    }

    setIsDeleting(true);
    
    try {
      // Replace placeholders in URL
      let url = deleteApiUrl
        .replace(':id', id)
        .replace(':org_id', org_id)
        .replace(':bank_id', bank_id);
      
      url = url.startsWith('http') ? url : `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}${url}`;

      const config = {
        headers: {
          'access-token': userAuthData?.['access-token'] || userAuthData?.access_token,
          uid: userAuthData?.uid,
          client: userAuthData?.client,
        },
      };

      let response;
      const method = deleteApiMethod.toUpperCase();
      
      if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
        const payload = buildDeletePayload ? buildDeletePayload(entityData) : {};
        response = await axios[method.toLowerCase()](url, payload, config);
      } else {
        response = await axios.delete(url, config);
      }

      setDeleteResponse(response.data);
      
      toast.success(intl.formatMessage({ id: 'DeletedSuccessfully' }) || 'Deleted successfully!', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        toastStyle: {
          backgroundColor: '#F1662D',
          color: 'white',
        },
      });

      // Redirect to back URL immediately after successful delete
      if (backUrl) {
        history.push(backUrl);
      }
    } catch (error) {
      const rawMsg = error?.response?.data?.message || error.toString();
      const errorMsg = messageTransformer ? messageTransformer(rawMsg) : rawMsg;
      const isArabicMsg = /[\u0600-\u06FF]/.test(errorMsg || '');
      toast.error(`${errorMsg}`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        style: {
          direction: isArabicMsg ? 'rtl' : 'ltr',
          textAlign: isArabicMsg ? 'right' : 'left',
          fontFamily: 'Cairo, sans-serif',
          fontWeight: 'bold',
        },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle edit navigation
  const handleEdit = () => {
    if (!editUrl) return;

    let url = editUrl
      .replace(':id', id)
      .replace(':org_id', org_id)
      .replace(':bank_id', bank_id);

    history.push(`${url}?came_from=one`);
  };

  // Render field value
  const renderFieldValue = (field) => {
    if (!entityData) return null;

    // Check if field should be hidden
    if (field.hide && field.hide(entityData, additionalData)) {
      return null;
    }

    // Custom render function
    if (field.render) {
      return field.render(entityData, additionalData, intl);
    }

    // Get value from data
    const value = entityData[field.name];

    // Handle different field types
    switch (field.type) {
      case 'date':
        return value ? new Date(value).toLocaleString(currentLanguageCode) : '-';
      
      case 'status':
        // Render status with icon
        if (value === 1) {
          return <CheckCircleIcon style={{ color: '#81D135' }} />;
        } else if (value === 2) {
          return <CheckCircleIcon style={{ color: 'black' }} />;
        } else if (value === 3) {
          return <CheckCircleIcon style={{ color: 'red' }} />;
        }
        return value;
      
      case 'custom':
        // Custom type should use render function
        return value;
      
      default:
        return value || '-';
    }
  };

  // Build title
  const title = buildTitle && entityData ? buildTitle(entityData, additionalData, intl) : '';

  // Build delete confirmation message
  const deleteConfirmMessage = buildDeleteConfirmMessage && entityData
    ? buildDeleteConfirmMessage(entityData, additionalData, currentLanguageCode, intl)
    : (currentLanguageCode === 'en-us'
      ? `Are You Sure You Want To Delete This Item?`
      : `هل أنت متأكد انك تريد حذف هذا العنصر؟`);

  // Check if delete is allowed
  const isDeleteAllowed = canDelete ? canDelete(entityData, additionalData) : true;

  return (
    <>
      <Box sx={{ marginTop: -5, padding: 4 }}>
        <Typography sx={{ color: '#252733', fontWeight: 'bold', marginBottom: '16px' }}>
          {intl.formatMessage({ id: pageTitle })}
        </Typography>
        
        {isFormLoading ? (
          <CircularProgress style={{ display: 'block' }} />
        ) : entityData ? (
          <>
            <div className='table-view-item-info' style={containerStyle}>
              <div className="name-and-actions-btns-sec">
                <h5 className="title-item-sec">
                  {title}
                </h5>
                <div className="actions-btns-sec">
                  {renderActionButtons && renderActionButtons(entityData, additionalData, intl)}
                  {hasWriteAccess && (
                    <>
                      {showEditButton && editUrl && (
                        <button
                          className="styleAddBtn"
                          style={{
                            position: 'inherit',
                            marginTop: '-10px',
                            paddingBottom: '7px',
                            paddingTop: '8px'
                          }}
                          onClick={handleEdit}
                        >
                          <EditIcon style={{ fontSize: '24px', paddingRight: '3px', paddingLeft: '3px' }} />
                          {intl.formatMessage({ id: 'edit' })}
                        </button>
                      )}
                      
                      {showDeleteButton && deleteApiUrl && isDeleteAllowed && (
                        <>
                          <button
                            className="styleAddBtn"
                            data-bs-toggle="modal"
                            style={{
                              backgroundColor: '#D54C5E',
                              position: 'inherit',
                              marginLeft: '5px',
                              marginRight: '5px',
                              marginTop: '-10px',
                              paddingBottom: '7px',
                              paddingTop: '8px'
                            }}
                            data-bs-target={`#deleteModal${id}`}
                          >
                            <DeleteIcon style={{ fontSize: '24px', paddingRight: '3px', paddingLeft: '3px' }} />
                            {intl.formatMessage({ id: 'Delete' })}
                          </button>
                          
                          {/* Delete Confirmation Modal */}
                          <div
                            className="modal fade"
                            style={{ zIndex: '9999969' }}
                            id={`deleteModal${id}`}
                            tabIndex={-1}
                            aria-labelledby="deleteModalLabel"
                            aria-hidden="true"
                          >
                            <div className="modal-dialog">
                              <div className="modal-content">
                                <div className="modal-header">
                                  <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                  ></button>
                                </div>
                                <div className="modal-body">
                                  <h5>{deleteConfirmMessage}</h5>
                                </div>
                                <div
                                  className="modal-footer"
                                  style={{ direction: currentLanguageCode === 'en-us' ? 'ltr' : 'rtl' }}
                                >
                                  <button
                                    type="button"
                                    className="btn btn-outline-dark"
                                    data-bs-dismiss="modal"
                                  >
                                    {intl.formatMessage({ id: 'Cancel' })}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger"
                                    data-bs-dismiss="modal"
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                  >
                                    {isDeleting
                                      ? intl.formatMessage({ id: 'Deleting' }) || 'Deleting...'
                                      : intl.formatMessage({ id: 'Delete' })}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {renderAboveData && renderAboveData(entityData, additionalData, intl)}

              <div className="data-view-sec">
                {fields.map((field) => {
                  // Check if field should be hidden
                  if (field.hide && field.hide(entityData, additionalData)) {
                    return null;
                  }

                  return (
                    <div key={field.name} className="one-info">
                      <div className="title-of-one-info">
                        {intl.formatMessage({ id: field.labelKey })}
                      </div>
                      <div className="value-of-one-info">
                        {renderFieldValue(field)}
                      </div>
                    </div>
                  );
                })}
              </div>
              {renderBelowData && renderBelowData(entityData, additionalData, intl)}
            </div>
          </>
        ) : (
          <Typography>No data found</Typography>
        )}
        
        <button
          className='cancelBtnStyle'
          style={{ marginTop: '20px' }}
          onClick={() => history.push(backUrl)}
        >
          {intl.formatMessage({ id: backButtonLabel })}
        </button>
      </Box>
      <ToastContainer />
    </>
  );
};

export default ViewEntity;
